// FIXED EnhancedApplicationForm.js - Replace your existing file with this

import React, { useState, useCallback, useMemo, memo } from 'react';

// Character limits configuration (stable reference)
const APPLICATION_LIMITS = {
  coverLetter: { min: 100, max: 1000 },
  whyInterested: { min: 50, max: 500 },
  relevantExperience: { min: 50, max: 800 },
  availability: { min: 10, max: 200 },
  additionalInfo: { min: 0, max: 300 }
};

// Validation helper (stable reference)
const validateApplicationForm = (form) => {
  const errors = {};
  
  Object.keys(APPLICATION_LIMITS).forEach(field => {
    const value = form[field] || '';
    const limits = APPLICATION_LIMITS[field];
    
    if (limits.min > 0 && value.length < limits.min) {
      errors[field] = `Minimum ${limits.min} characters required`;
    }
    if (value.length > limits.max) {
      errors[field] = `Maximum ${limits.max} characters allowed`;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Character counter component - fully memoized
const CharacterCounter = memo(({ current, max, min, fieldName }) => {
  const percentage = (current / max) * 100;
  const isNearLimit = percentage > 80;
  const isAtLimit = percentage >= 100;
  const isBelowMin = current < min && current > 0;
  
  let colorClass = 'text-gray-500';
  if (isAtLimit) colorClass = 'text-red-500';
  else if (isNearLimit) colorClass = 'text-yellow-600';
  else if (isBelowMin) colorClass = 'text-orange-500';
  else if (current >= min) colorClass = 'text-green-600';
  
  return (
    <div className={`text-sm ${colorClass}`}>
      <div className="flex justify-between items-center">
        <span>
          {current}/{max} characters
          {min > 0 && current < min && (
            <span className="ml-1 text-orange-600">
              (minimum {min})
            </span>
          )}
        </span>
        {isAtLimit && (
          <span className="text-red-500 font-medium">Limit reached!</span>
        )}
        {isBelowMin && (
          <span className="text-orange-500 font-medium">
            {min - current} more needed
          </span>
        )}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
        <div 
          className={`h-1 rounded-full transition-all duration-200 ${
            isAtLimit ? 'bg-red-500' : 
            isNearLimit ? 'bg-yellow-500' : 
            isBelowMin ? 'bg-orange-500' : 
            'bg-green-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        ></div>
      </div>
    </div>
  );
});

// CRITICAL FIX: Individual field components to prevent re-renders
const FormField = memo(({ 
  label, 
  fieldName, 
  value, 
  onChange, 
  rows, 
  placeholder, 
  limits, 
  validationError,
  helpText 
}) => {
  // Create a stable onChange handler for this specific field
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    if (newValue.length <= limits.max) {
      onChange(fieldName, newValue);
    }
  }, [onChange, fieldName, limits.max]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {helpText && (
          <span className="text-xs text-gray-500 ml-1">
            ({helpText})
          </span>
        )}
      </label>
      <textarea
        value={value}
        onChange={handleChange}
        rows={rows}
        maxLength={limits.max}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none ${
          validationError ? 'border-red-300' : 'border-gray-300'
        }`}
        placeholder={placeholder}
      />
      <div className="mt-2">
        <CharacterCounter 
          current={value.length}
          max={limits.max}
          min={limits.min}
          fieldName={fieldName}
        />
        {validationError && (
          <p className="text-red-500 text-xs mt-1">{validationError}</p>
        )}
      </div>
    </div>
  );
});

// Main Enhanced Application Form Component
const EnhancedApplicationForm = memo(({ 
  applicationForm, 
  setApplicationForm, 
  onSubmit, 
  submitting = false,
  selectedPosition,
  onCancel
}) => {
  const [validationErrors, setValidationErrors] = useState({});

  // CRITICAL FIX: Create a stable field update function
  const handleFieldChange = useCallback((fieldName, value) => {
    setApplicationForm(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error for this field if it becomes valid
    if (validationErrors[fieldName]) {
      const newForm = { ...applicationForm, [fieldName]: value };
      const validation = validateApplicationForm(newForm);
      if (!validation.errors[fieldName]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    }
  }, [setApplicationForm, validationErrors, applicationForm]);

  // Submit handler
  const handleSubmit = useCallback(() => {
    const validation = validateApplicationForm(applicationForm);
    
    if (validation.isValid) {
      setValidationErrors({});
      onSubmit();
    } else {
      setValidationErrors(validation.errors);
    }
  }, [applicationForm, onSubmit]);

  // Check if form is submittable
  const canSubmit = useMemo(() => {
    const validation = validateApplicationForm(applicationForm);
    return validation.isValid && applicationForm.coverLetter.trim().length > 0;
  }, [applicationForm]);

  return (
    <div className="space-y-6">
      {/* Position Summary */}
      {selectedPosition && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="font-semibold mb-2">{selectedPosition.name}</h3>
          <p className="text-sm text-gray-600 mb-2">{selectedPosition.description}</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><strong>Location:</strong> {selectedPosition.location}</div>
            <div><strong>Type:</strong> {selectedPosition.type}</div>
            <div><strong>Duration:</strong> {selectedPosition.duration}</div>
            <div><strong>Paid:</strong> {selectedPosition.isPaid ? 'Yes' : 'No'}</div>
          </div>
        </div>
      )}

      {/* Form Fields using stable FormField components */}
      <FormField
        label="Cover Letter *"
        fieldName="coverLetter"
        value={applicationForm.coverLetter}
        onChange={handleFieldChange}
        rows={5}
        placeholder="Introduce yourself, explain your interest in this position, and highlight what makes you a great candidate. Be specific about why you want to work with this company..."
        limits={APPLICATION_LIMITS.coverLetter}
        validationError={validationErrors.coverLetter}
        helpText="This is your chance to make a great first impression"
      />

      <FormField
        label="Why are you interested in this position? *"
        fieldName="whyInterested"
        value={applicationForm.whyInterested}
        onChange={handleFieldChange}
        rows={3}
        placeholder="What specifically attracts you to this role and company? What do you hope to learn or achieve?"
        limits={APPLICATION_LIMITS.whyInterested}
        validationError={validationErrors.whyInterested}
        helpText="Show your research and genuine interest"
      />

      <FormField
        label="Relevant Experience *"
        fieldName="relevantExperience"
        value={applicationForm.relevantExperience}
        onChange={handleFieldChange}
        rows={4}
        placeholder="Describe any relevant experience, projects, coursework, or skills that relate to this position. Include specific examples and achievements..."
        limits={APPLICATION_LIMITS.relevantExperience}
        validationError={validationErrors.relevantExperience}
        helpText="Include projects, coursework, work experience, or skills"
      />

      <FormField
        label="Availability *"
        fieldName="availability"
        value={applicationForm.availability}
        onChange={handleFieldChange}
        rows={2}
        placeholder="When are you available to start? Any scheduling constraints or commitments?"
        limits={APPLICATION_LIMITS.availability}
        validationError={validationErrors.availability}
        helpText="When can you start? Any constraints?"
      />

      <FormField
        label="Additional Information"
        fieldName="additionalInfo"
        value={applicationForm.additionalInfo}
        onChange={handleFieldChange}
        rows={2}
        placeholder="Any additional information, links to portfolio/projects, or questions for the employer..."
        limits={APPLICATION_LIMITS.additionalInfo}
        validationError={validationErrors.additionalInfo}
        helpText="Optional - anything else you'd like to share"
      />

      {/* Form Summary */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Application Summary</h4>
        <div className="grid grid-cols-2 gap-4 text-xs text-blue-800">
          <div>Cover Letter: {applicationForm.coverLetter.length}/{APPLICATION_LIMITS.coverLetter.max}</div>
          <div>Why Interested: {applicationForm.whyInterested.length}/{APPLICATION_LIMITS.whyInterested.max}</div>
          <div>Experience: {applicationForm.relevantExperience.length}/{APPLICATION_LIMITS.relevantExperience.max}</div>
          <div>Availability: {applicationForm.availability.length}/{APPLICATION_LIMITS.availability.max}</div>
        </div>
        <div className="mt-2 text-xs text-blue-700">
          {canSubmit ? (
            <span className="text-green-700 font-medium">✓ Ready to submit!</span>
          ) : (
            <span className="text-orange-700">Please complete all required fields with minimum character requirements.</span>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {submitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </>
          ) : (
            'Submit Application'
          )}
        </button>
      </div>
    </div>
  );
});

// Set display names for debugging
CharacterCounter.displayName = 'CharacterCounter';
FormField.displayName = 'FormField';
EnhancedApplicationForm.displayName = 'EnhancedApplicationForm';

export { EnhancedApplicationForm, APPLICATION_LIMITS, validateApplicationForm };